import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import ListCard from "../../common/ListCard";
import RestaurantCard from "../mobile/RestaurantCard";
import { slugify } from "../../../util/slugify";
import { stateAbbrev } from "../../../util/stateAbbrev";

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 16px;
`;

const Title = styled.h1`
  font-size: 1.6em;
  margin: 4px 0 6px;
  color: #1a1a1a;
`;

const CuisineRibbon = styled.div`
  color: var(--color-text-secondary, #666);
  font-size: 0.9em;
  margin: 0 0 14px;
  letter-spacing: 0.01em;
`;

const Prose = styled.div`
  p {
    line-height: 1.55;
    color: #333;
    margin: 0 0 14px;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.15em;
  margin: 24px 0 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin: 16px 0;
`;

const OverviewPanel = ({ cluster, lists, sampleRestaurants, state, city }) => {
  const navigate = useNavigate();
  const abbr = stateAbbrev(cluster.state);
  const heading = `${cluster.cityName}, ${abbr}`;
  const proseParagraphs = (cluster.seoProse || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const cuisineRibbon = (cluster.topCuisines || [])
    .filter(Boolean)
    .slice(0, 5)
    .map((c) => c.replace(/\b\w/g, (l) => l.toUpperCase()))
    .join(" · ");

  const mapHref = `/cities/${state}/${city}`;

  return (
    <Container>
      <Title>Best Vegan Food in {heading}</Title>
      {cuisineRibbon && <CuisineRibbon>{cuisineRibbon}</CuisineRibbon>}
      <Prose>
        {proseParagraphs.length > 0 ? (
          proseParagraphs.map((p, i) => <p key={i}>{p}</p>)
        ) : (
          <p>
            Explore vegan-friendly restaurants in {heading}. Browse the map
            or curated lists below.
          </p>
        )}
      </Prose>
      {sampleRestaurants && sampleRestaurants.length > 0 && (
        <Grid>
          {sampleRestaurants.map((r) => (
            <RestaurantCard
              key={r._id}
              restaurant={r}
              leafRating={r.leafRating}
              showDistance={false}
              onClick={() =>
                navigate(`/details/${r._id}/${r.slug || slugify(r.name)}`)
              }
            />
          ))}
        </Grid>
      )}
      <SectionHeading>Explore further</SectionHeading>
      <ListCard
        to={mapHref}
        title="See all restaurants on the map"
        description={`Browse every vegan-friendly spot in ${cluster.cityName} on an interactive map.`}
        icon="fas fa-map"
        iconLabel="Map view"
      />
      {lists && lists.length > 0 && (
        <>
          <SectionHeading>Curated lists for {heading}</SectionHeading>
          {lists.map((l) => (
            <ListCard
              key={l.slug}
              to={`/lists/${l.slug}`}
              title={l.name}
              description={l.seoMetaDescription || l.description}
              cityLabel={heading}
            />
          ))}
        </>
      )}
    </Container>
  );
};

OverviewPanel.propTypes = {
  cluster: PropTypes.shape({
    cityName: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    seoProse: PropTypes.string,
    topCuisines: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  lists: PropTypes.array,
  sampleRestaurants: PropTypes.array,
  state: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
};

export default OverviewPanel;
